/**
 * Auto-buffer a degenerate geometry into a small rectangular Polygon.
 *
 * When a saved field only has a centroid point (as a `Point` geometry, or a
 * `Polygon` ring with fewer than 3 coordinates — i.e. not a real polygon boundary),
 * this utility creates a small rectangular farm plot around the point so the
 * user can see + edit vertices on the map.
 *
 * The buffer size is small (~167 m) to represent a minimal farm plot.
 * Adjust `bufferDeg` to control the side length in decimal degrees.
 */

const DEFAULT_BUFFER_DEG = 0.0015;

export interface GeoJsonGeometry {
  type: string;
  coordinates: any;
}

/**
 * Check whether a geometry represents a Point (lon/lat centroid),
 * e.g. `{"type": "Point", "coordinates": [lng, lat]}`.
 */
export function isPointGeometry(geometry: GeoJsonGeometry | null | undefined): boolean {
  return !!(
    geometry &&
    geometry.type === 'Point' &&
    Array.isArray(geometry.coordinates) &&
    geometry.coordinates.length >= 2
  );
}

/**
 * Check whether a Polygon geometry is degenerate — i.e. its outer ring contains
 * fewer than 3 coordinate pairs, so it cannot form a real area boundary.
 *
 * Catches cases like:
 * ```json
 * {"type": "Polygon", "coordinates": [[[lng, lat]]]}
 * ```
 * where the API stores a centroid disguised as a Polygon.
 */
export function isDegeneratePolygon(geometry: GeoJsonGeometry | null | undefined): boolean {
  if (!geometry || geometry.type !== 'Polygon') return false;
  if (!Array.isArray(geometry.coordinates)) return false;

  const outerRing = geometry.coordinates[0];
  if (!Array.isArray(outerRing)) return false;

  // A valid polygon ring needs 3+ distinct coordinate pairs.
  // If it has 0, 1, or 2, it's degenerate.
  return outerRing.length < 3;
}

/**
 * Return true if the geometry is either a bare `Point` OR a degenerate
 * `Polygon` that needs buffering.
 */
export function isDegenerateOrPointGeometry(geometry: GeoJsonGeometry | null | undefined): boolean {
  return isPointGeometry(geometry) || isDegeneratePolygon(geometry);
}

/**
 * Extract a single [lng, lat] centroid from a bare Point geometry or from
 * the first coordinate of a degenerate Polygon ring.
 *
 * Returns `null` when the geometry doesn't match either form.
 */
export function extractCentroid(geometry: GeoJsonGeometry | null | undefined): [number, number] | null {
  if (isPointGeometry(geometry)) {
    return [geometry.coordinates[0], geometry.coordinates[1]];
  }
  if (isDegeneratePolygon(geometry)) {
    const pos = geometry.coordinates[0][0];
    if (Array.isArray(pos) && pos.length >= 2) {
      return [pos[0], pos[1]];
    }
  }
  return null;
}

/**
 * Create a rectangular Polygon by buffering around a [lng, lat] centroid.
 *
 * @returns A GeoJSON Polygon geometry with 4 vertices forming a rectangle.
 */
export function createBufferPolygon(
  lng: number,
  lat: number,
  bufferDeg: number = DEFAULT_BUFFER_DEG
): GeoJsonGeometry {
  const half = bufferDeg / 2;
  return {
    type: 'Polygon',
    coordinates: [
      [
        [lng - half, lat - half],
        [lng + half, lat - half],
        [lng + half, lat + half],
        [lng - half, lat + half],
        [lng - half, lat - half], // close ring
      ],
    ],
  };
}

/**
 * Convert a Point or degenerate Polygon geometry into a proper rectangular
 * Polygon by buffering around its centroid.
 *
 * - Input `{"type": "Point", "coordinates": [lng, lat]}` → buffered rectangle
 * - Input `{"type": "Polygon", "coordinates": [[[lng, lat]]]}` → buffered rectangle
 * - Input with a real polygon (3+ coords in ring) → returned as-is
 */
export function bufferPointToPolygon(
  geometry: GeoJsonGeometry,
  bufferDeg: number = DEFAULT_BUFFER_DEG
): GeoJsonGeometry {
  const centroid = extractCentroid(geometry);
  if (!centroid) {
    // It's already a real polygon (or unsupported type) — pass through
    return geometry;
  }
  return createBufferPolygon(centroid[0], centroid[1], bufferDeg);
}

/**
 * Format an approximate area string for the buffered polygon for display.
 */
export function formatBufferAreaMessage(bufferDeg: number = DEFAULT_BUFFER_DEG): string {
  const sideM = Math.round(bufferDeg * 111_000); // rough deg→m at equator
  const areaHa = ((sideM * sideM) / 10_000).toFixed(1);
  return `${sideM} m × ${sideM} m (≈ ${areaHa} ha)`;
}
